import cx from 'classnames';
import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../../../../core/Tracking/Amplitude/Amplitude';
import { Button } from '../../../../../../design-system/components/form/button/Button';
import { Agency } from '../../../../../../resources/agency/domain/types/Agency';
import { Destination } from '../../../../../../resources/destination/domain/types/destination';
import { useLocale } from '../../../../../../shared/hooks/useLocale';
import { useUrlGenerator } from '../../../../../../shared/hooks/useUrlGenerator';
import { PickDomain } from '../../../../../../shared/utilities/types/PickDomain';
import { getIconByCertification } from '../shared/utilities/getIconByCertification';

import style from './Engagement.module.css';

type EngagementProps = {
    agency: PickDomain<
        Agency,
        'certifications' | 'id' | 'slug' | 'betterTripIndex' | 'isAvailable',
        {
            responsibleTourismDescription: Agency['responsibleTourismDescription'] | null;
        }
    >;
    destination: PickDomain<Destination, 'slug'>;
};

export const Engagement = ({ agency, destination }: EngagementProps) => {
    const { formatMessage } = useIntl();
    const { generatePath } = useUrlGenerator();
    const { trackEvent } = useTrackEventAmplitude();
    const { locale } = useLocale();

    const handleClickTripForm = () => {
        trackEvent((ampli, defaultProperties) => {
            ampli.tripformCreationStartedFromAgency({
                ...defaultProperties,
                agency_id: agency.id,
                agency_slug: agency.slug,
                agency_better_trip_index: agency.betterTripIndex,
                block: 'no_match',
            });
        });
    };

    return (
        <div className={cx('main-container', style.engagementSection)}>
            <div className={style.wrapper}>
                <div className={style.contentBlock}>
                    {/* eslint-disable-next-line local-rules/no-global-typography-classes -- ADR-0017: à migrer vers <Typography> */}
                    <p className={cx(style.subtitle, 'font-body-bold')}>
                        {formatMessage({ id: 'page.agency.engagement-subtitle' })}
                    </p>
                    {/* eslint-disable-next-line local-rules/no-global-typography-classes -- ADR-0017: à migrer vers <Typography> */}
                    <h2 className={cx('font-secondary-l-bold', style.title)}>
                        {formatMessage({ id: 'page.agency.engagement-title' })}
                    </h2>
                    {/* eslint-disable-next-line local-rules/no-global-typography-classes -- ADR-0017: à migrer vers <Typography> */}
                    <p className="font-body-regular">{agency.responsibleTourismDescription}</p>
                </div>
                {agency.certifications.length ? (
                    <div className={style.certificationsContent}>
                        {/* eslint-disable-next-line local-rules/no-global-typography-classes -- ADR-0017: à migrer vers <Typography> */}
                        <p className="font-body-bold">
                            {formatMessage(
                                { id: 'page.agency.certifications' },
                                { certificationsCount: agency.certifications.length },
                            )}
                        </p>
                        <div className={style.certificationsLogos}>
                            {getIconByCertification(agency.certifications, locale)}
                        </div>
                    </div>
                ) : null}
            </div>

            <Button
                disabled={!agency.isAvailable}
                obfuscate
                href={generatePath('trip_form', {
                    destinationSlug: destination.slug,
                    agency: agency.id,
                })}
                onClick={handleClickTripForm}
            >
                {formatMessage({ id: 'page.agency.header-cta' })}
            </Button>
        </div>
    );
};
